import type { PortalKind } from '@/lib/auth/token-storage'

export interface StoredPortalAccount {
  avatar?: string
  email?: string
  id?: string
  name?: string
  role?: string
  status?: string
}

const fallbackName: Record<PortalKind, string> = {
  admin: 'Super Admin',
  user: '用户',
}

const loginPrefillEmailKey: Record<PortalKind, string> = {
  admin: 'admin_login_prefill_email',
  user: 'user_login_prefill_email',
}

const userPostLoginReturnPathKey = 'user_post_login_return_path'
const wecomBindingReturnPathPattern = /^\/im\/wecom\/bind\/[A-Za-z0-9._~-]+(?:\?wecom_auth=1)?$/
const wecomAuthReturnPath = '/signin/wecom'
export const wecomBindingLoginResumeMarker = 'wecom_binding'

export const portalAccountChangedEventName = 'portal-account-changed'

export interface PortalAccountChangedDetail {
  portal: PortalKind
}

function readOptionalString(value: unknown) {
  return typeof value === 'string' ? value : undefined
}

function writeStoredPortalAccount(portal: PortalKind, account: StoredPortalAccount) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(`${portal}_data`, JSON.stringify(account))
  window.dispatchEvent(
    new CustomEvent<PortalAccountChangedDetail>(portalAccountChangedEventName, {
      detail: { portal },
    }),
  )
}

/**
 * 读取 shell 展示和后台改密需要的最小账号快照。
 * localStorage 里的用户对象来自登录响应，读取时仍按 unknown 处理并逐字段收窄。
 */
export function readStoredPortalAccount(portal: PortalKind): StoredPortalAccount {
  const fallbackAccount: StoredPortalAccount = {
    name: fallbackName[portal],
  }

  if (typeof window === 'undefined') {
    return fallbackAccount
  }

  const rawAccount = window.localStorage.getItem(`${portal}_data`)

  if (!rawAccount) {
    return fallbackAccount
  }

  try {
    const parsedAccount = JSON.parse(rawAccount) as Partial<StoredPortalAccount>

    return {
      avatar: readOptionalString(parsedAccount.avatar),
      email: readOptionalString(parsedAccount.email),
      id: readOptionalString(parsedAccount.id),
      name: readOptionalString(parsedAccount.name),
      role: readOptionalString(parsedAccount.role),
      status: readOptionalString(parsedAccount.status),
    }
  } catch {
    return fallbackAccount
  }
}

/**
 * 更新 shell 账号快照，并通知同一标签页内的 shell 重新读取。
 * 后端仍是资料权威；本地快照只服务侧边栏和 profile menu 的即时显示。
 */
export function updateStoredPortalAccount(
  portal: PortalKind,
  account: Partial<StoredPortalAccount>,
) {
  const current = readStoredPortalAccount(portal)
  writeStoredPortalAccount(portal, {
    ...current,
    ...account,
  })
}

/**
 * 临时保存登录邮箱，用于强制改密或重设密码后回到登录页时预填。
 * 这是便利能力，不参与认证状态判断。
 */
export function persistPortalLoginPrefillEmail(portal: PortalKind, email: string | undefined) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const normalizedEmail = email?.trim()
    if (!normalizedEmail) {
      window.sessionStorage.removeItem(loginPrefillEmailKey[portal])
      return
    }

    window.sessionStorage.setItem(loginPrefillEmailKey[portal], normalizedEmail)
  } catch {
    // 登录预填是便利能力，浏览器禁用 sessionStorage 时不影响主流程。
  }
}

/**
 * 读取后立即删除登录邮箱预填值，避免长期暴露在浏览器 storage 中。
 */
export function consumePortalLoginPrefillEmail(portal: PortalKind) {
  if (typeof window === 'undefined') {
    return ''
  }

  try {
    const key = loginPrefillEmailKey[portal]
    const email = window.sessionStorage.getItem(key)?.trim() ?? ''
    window.sessionStorage.removeItem(key)
    return email
  } catch {
    return ''
  }
}

/**
 * 仅接受企微绑定与企微 Auth 恢复 path，避免登录后被 query 参数带到站外。
 */
export function normalizeUserPostLoginReturnPath(value: string | null | undefined) {
  const candidate = value?.trim() ?? ''
  return wecomBindingReturnPathPattern.test(candidate) || candidate === wecomAuthReturnPath
    ? candidate
    : undefined
}

export function persistUserPostLoginReturnPath(value: string | null | undefined) {
  if (typeof window === 'undefined') return
  const returnPath = normalizeUserPostLoginReturnPath(value)
  try {
    if (returnPath) {
      window.sessionStorage.setItem(userPostLoginReturnPathKey, returnPath)
    } else {
      window.sessionStorage.removeItem(userPostLoginReturnPathKey)
    }
  } catch {
    // return path 只改善登录衔接，sessionStorage 不可用时仍可正常登录。
  }
}

export function readUserPostLoginReturnPath() {
  if (typeof window === 'undefined') return undefined
  try {
    return normalizeUserPostLoginReturnPath(
      window.sessionStorage.getItem(userPostLoginReturnPathKey),
    )
  } catch {
    return undefined
  }
}

export function appendUserLoginResumeMarker(path: string) {
  if (!readUserPostLoginReturnPath()) return path
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}resume=${wecomBindingLoginResumeMarker}`
}

export function consumeUserPostLoginReturnPath() {
  if (typeof window === 'undefined') return undefined
  try {
    const returnPath = normalizeUserPostLoginReturnPath(
      window.sessionStorage.getItem(userPostLoginReturnPathKey),
    )
    window.sessionStorage.removeItem(userPostLoginReturnPathKey)
    return returnPath
  } catch {
    return undefined
  }
}
